import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold ring-offset-background transition-all duration-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-gradient-rose text-primary-foreground shadow-rose hover:shadow-glow hover:scale-[1.05] active:scale-[0.98] relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:-translate-x-full hover:before:translate-x-full before:transition-transform before:duration-1000",
        premium: "bg-gradient-rose-glow text-primary-foreground shadow-glow hover:shadow-rose animate-glow-pulse border-2 border-primary-glow/50",
        glass: "bg-card-glass/40 backdrop-blur-glass border border-border-rose/30 text-foreground hover:bg-card-glass/60 hover:border-border-rose/60 shadow-depth hover:shadow-rose",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-depth",
        outline: "border-2 border-border-rose/40 bg-transparent text-foreground hover:bg-primary/10 hover:border-border-rose hover:text-primary hover:shadow-rose",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-depth",
        ghost: "hover:bg-primary/10 hover:text-primary",
        link: "text-primary underline-offset-4 hover:underline hover:text-primary-glow",
        gold: "bg-gradient-gold text-accent-foreground shadow-gold hover:shadow-glow hover:scale-[1.05] active:scale-[0.98]",
      },
      size: {
        default: "h-12 px-6 py-3",
        sm: "h-9 rounded-md px-4 text-xs",
        lg: "h-14 rounded-xl px-10 text-base",
        xl: "h-16 rounded-xl px-12 text-lg",
        icon: "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
